import React, { useState, useEffect } from "react";
import { Card, Typography, Space, Tag, Button, Select, Input, InputNumber, Table, Alert, Collapse, Timeline, Divider, Tabs, Modal, Checkbox, Tooltip, Empty, Radio, message } from "antd";
import { CheckOutlined, UndoOutlined, SearchOutlined, InfoCircleOutlined, HistoryOutlined, EditOutlined, CloseOutlined, CalculatorOutlined, AirplaneOutlined, ExpandOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import { flightOperationFlightService } from "../../../../../api/enhancedApi";
import PermissionGuard from "../../../../../components/auth/PermissionGuard";
import AircraftTag from "../../../../../components/AircraftTag";
import AircraftSelector from "../../../../../components/common/AircraftSelector";
import FlightTimeDisplay from "../../../../../components/DateTimeDisplay/FlightTimeDisplay";
import { useAirports } from "../../../../../hooks/useAirports";
import { ALLOCATION_RULE, ALLOCATION_RULE_LABELS } from "../../../constants";
import { flightSelectionFieldUpdates, hasPendingFlightSelectionChange, isLineItemModified, isLineItemValidationStale, lineItemFieldPath } from "../../utils/reviewPayloadSemantics";
import { calculateFuelQuantityAllocation, inferDefaultProductCodes, ALLOCATION_DEGRADED_REASONS } from "../../utils/allocationCalculator";

dayjs.extend(utc);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const ReconciliationTools = () => {
    const { selectedLineItem, clearSelectedLineItem, updateReconciliationData, invoices, reconciliationState } = useInvoiceReview();
    const { airports: airportsMap } = useAirports(); // 获取机场数据用于时区转换
    const [activeTab, setActiveTab] = useState("allocation");
    const [flightSearchModalVisible, setFlightSearchModalVisible] = useState(false);
    const [flights, setFlights] = useState([]);
    const [flightsLoading, setFlightsLoading] = useState(false);
    const [tempSelectedFlights, setTempSelectedFlights] = useState([]); // 模态框临时选择状态

    // 获取当前选中的行项目（实时数据）
    const currentItem = React.useMemo(() => {
        if (!selectedLineItem || selectedLineItem.invoiceIndex === null) return null;
        const { invoiceIndex, sectionIndex, itemIndex } = selectedLineItem;
        return invoices?.[invoiceIndex]?.sections?.[sectionIndex]?.line_items?.[itemIndex];
    }, [selectedLineItem, invoices]);

    // 从当前项目派生状态
    const allocationType = currentItem?.allocation_type || "DIRECT";
    // 注意：规则为空时必须显示为"未选中"，不能假装选中默认值——
    // 否则用户点击"已选中"的选项不会触发 onChange，allocation_rule 永远写不进去
    const allocationRule = currentItem?.allocation_rule || null;
    const selectedFlights = currentItem?.matched_flights || [];
    const currentFieldPath = selectedLineItem && selectedLineItem.invoiceIndex !== null
        ? lineItemFieldPath(selectedLineItem.invoiceIndex, selectedLineItem.sectionIndex, selectedLineItem.itemIndex)
        : null;
    const currentItemModified = currentFieldPath ? isLineItemModified(reconciliationState?.dirtyFields, currentFieldPath) : false;
    const currentValidationStale = currentFieldPath ? isLineItemValidationStale(reconciliationState?.dirtyFields, currentFieldPath) : false;
    const currentAnomalies = currentValidationStale ? [] : (currentItem?.anomalies || []);
    const hasUnappliedAdvancedFlightSelection = flightSearchModalVisible && hasPendingFlightSelectionChange(selectedFlights, tempSelectedFlights);

    // BY_FUEL_QUANTITY 分摊预览所需上下文：聚合同发票全部 section 的 line_items（doc 70 §2.2）
    const invoiceLineItems = React.useMemo(() => {
        if (!selectedLineItem || selectedLineItem.invoiceIndex === null) return [];
        const invoice = invoices?.[selectedLineItem.invoiceIndex];
        if (!invoice?.sections) return [];
        return invoice.sections.flatMap((section) => (Array.isArray(section.line_items) ? section.line_items : []));
    }, [invoices, selectedLineItem]);

    // 关联航班 id → 航班号映射（用于分摊预览的可读展示）
    const flightNumberMap = React.useMemo(() => {
        const map = {};
        (currentItem?.matched_flights || []).forEach((f) => {
            if (f && f.id !== undefined && f.id !== null) {
                map[String(f.id)] = f.flight_number || f.flight_no || `航班 ${f.id}`;
            }
        });
        return map;
    }, [currentItem]);

    // BY_FUEL_QUANTITY 实时分摊预览：与后端 _handle_fuel_quantity_allocation 算法等价（doc 70 §6.3）
    const fuelQuantityPreview = React.useMemo(() => {
        if (!currentItem || currentItem.allocation_rule !== ALLOCATION_RULE.BY_FUEL_QUANTITY) return null;
        return calculateFuelQuantityAllocation(currentItem, invoiceLineItems);
    }, [currentItem, invoiceLineItems]);

    // 快捷筛选条件状态
    const [quickFilters, setQuickFilters] = useState({
        aircraft_registration: { enabled: true, value: "" },
        flight_number: { enabled: true, value: "" },
        flight_date: { enabled: true, value: "" },
        departure_airport: { enabled: false, value: "" },
        arrival_airport: { enabled: false, value: "" },
    });

    // 根据选中的行项目自动设置搜索条件
    useEffect(() => {
        if (currentItem) {
            const additionalInfo = currentItem.additional_info;
            if (additionalInfo) {
                // 从 additional_info 提取筛选条件
                const newFilters = {
                    aircraft_registration: {
                        enabled: true,
                        value: additionalInfo.aircraft_registration || "",
                    },
                    flight_number: {
                        enabled: true,
                        value: additionalInfo.flight_number || "",
                    },
                    flight_date: {
                        enabled: true,
                        value: additionalInfo.flight_date || "",
                    },
                    departure_airport: {
                        enabled: false,
                        value: additionalInfo.departure_airport || "",
                    },
                    arrival_airport: {
                        enabled: false,
                        value: additionalInfo.arrival_airport || "",
                    },
                };

                setQuickFilters(newFilters);

                // 自动执行搜索
                executeSearch(newFilters);
            }
        }
    }, [currentItem?.item_id]); // 仅当项目ID变化时重新初始化搜索

    // 筛选条件变化时自动执行搜索
    useEffect(() => {
        // 检查是否有任何启用的筛选条件
        const hasEnabledFilters = Object.values(quickFilters).some((filter) => filter.enabled && filter.value);

        if (hasEnabledFilters) {
            // 使用防抖，避免频繁搜索
            const timer = setTimeout(() => {
                executeSearch(quickFilters);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [quickFilters]);

    // 获取日期范围（前后三天）
    const getDateRange = (dateStr) => {
        const date = new Date(dateStr);
        const startDate = new Date(date);
        startDate.setDate(date.getDate() - 3);
        const endDate = new Date(date);
        endDate.setDate(date.getDate() + 3);

        return {
            start: startDate.toISOString().split("T")[0],
            end: endDate.toISOString().split("T")[0],
        };
    };

    // 根据快捷筛选条件执行搜索
    const executeSearch = async (filters = quickFilters) => {
        setFlightsLoading(true);
        try {
            const searchParams = {
                page: 1,
                page_size: 50,
                status: "COMPLETED,SCHEDULED",
            };

            // 根据启用的筛选条件添加参数
            if (filters.aircraft_registration.enabled && filters.aircraft_registration.value) {
                searchParams.aircraft_reg = filters.aircraft_registration.value.trim();
            }

            if (filters.flight_number.enabled && filters.flight_number.value) {
                searchParams.flight_number = filters.flight_number.value.trim();
            }

            if (filters.flight_date.enabled && filters.flight_date.value) {
                const dateRange = getDateRange(filters.flight_date.value);
                searchParams.start_date = dateRange.start;
                searchParams.end_date = dateRange.end;
            }

            if (filters.departure_airport.enabled && filters.departure_airport.value) {
                searchParams.departure_airport = filters.departure_airport.value.trim();
            }

            if (filters.arrival_airport.enabled && filters.arrival_airport.value) {
                searchParams.arrival_airport = filters.arrival_airport.value.trim();
            }

            console.log("航班搜索参数:", searchParams);

            const response = await flightOperationFlightService.getFlightOperations(searchParams);
            console.log("航班搜索响应:", response);

            // 处理不同的响应格式
            let flightData = [];
            if (response.data && response.data.items) {
                flightData = response.data.items;
            } else if (response.items) {
                flightData = response.items;
            } else if (Array.isArray(response)) {
                flightData = response;
            }

            console.log("解析后的航班数据:", flightData);
            setFlights(flightData);

            if (flightData.length === 0) {
                // message.info("未找到匹配的航班，尝试调整筛选条件");
            }
        } catch (error) {
            console.error("搜索航班失败:", error);
            message.error("搜索航班失败，请稍后重试");
            setFlights([]);
        } finally {
            setFlightsLoading(false);
        }
    };

    // 搜索航班（保留兼容性）
    const searchFlights = async (params = {}) => {
        setFlightsLoading(true);
        try {
            const searchParams = {
                page: 1,
                page_size: 10,
                status: "COMPLETED,SCHEDULED", // 查询已完成和计划中的航班
                ...params,
            };

            const response = await flightOperationFlightService.getFlightOperations(searchParams);
            console.log("航班搜索响应:", response);

            // 处理不同的响应格式
            let flightData = [];
            if (response.data && response.data.items) {
                flightData = response.data.items;
            } else if (response.items) {
                flightData = response.items;
            } else if (Array.isArray(response)) {
                flightData = response;
            }

            console.log("解析后的航班数据:", flightData);
            setFlights(flightData);
        } catch (error) {
            console.error("搜索航班失败:", error);
            message.error("搜索航班失败，请稍后重试");
            setFlights([]);
        } finally {
            setFlightsLoading(false);
        }
    };

    const applyFlightSelection = (flightsToApply) => {
        if (!selectedLineItem) return;
        const { invoiceIndex, sectionIndex, itemIndex } = selectedLineItem;
        const fieldPath = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;
        const updates = flightSelectionFieldUpdates(fieldPath, flightsToApply);
        updateReconciliationData(updates.matchedFlightIdsPath, updates.matchedFlightIds);
        updateReconciliationData(updates.matchedFlightsPath, updates.matchedFlights);
    };

    // 处理航班选择 (实时更新)
    const handleFlightSelection = (flight, checked) => {
        if (!selectedLineItem) return;

        let newSelectedFlights = [];
        if (allocationType === "DIRECT") {
            // 直接匹配模式，只能选择一个航班
            newSelectedFlights = checked ? [flight] : [];
        } else {
            // 分摊模式，可以选择多个航班
            if (checked) {
                newSelectedFlights = [...selectedFlights, flight];
            } else {
                newSelectedFlights = selectedFlights.filter((f) => f.id !== flight.id);
            }
        }

        applyFlightSelection(newSelectedFlights);
    };

    const closeAdvancedFlightSearch = () => {
        if (!hasUnappliedAdvancedFlightSelection) {
            setFlightSearchModalVisible(false);
            return;
        }

        Modal.confirm({
            title: "高级搜索选择尚未应用",
            content: "当前弹窗中的航班勾选还没有写入明细行。请选择“应用选择”写入本行，或放弃这次临时选择。",
            okText: "应用选择",
            cancelText: "放弃选择",
            onOk: () => {
                applyFlightSelection(tempSelectedFlights);
                setFlightSearchModalVisible(false);
            },
            onCancel: () => {
                setTempSelectedFlights(selectedFlights);
                setFlightSearchModalVisible(false);
            },
        });
    };

    // 处理分摊类型变更 (实时更新)
    const handleAllocationTypeChange = (e) => {
        if (!selectedLineItem) return;
        const { invoiceIndex, sectionIndex, itemIndex } = selectedLineItem;
        const fieldPath = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;

        const newType = e.target.value;
        updateReconciliationData(`${fieldPath}.allocation_type`, newType);

        if (newType === "DIRECT") {
            // 如果切换到 DIRECT 且有多个航班，保留第一个
            if (selectedFlights.length > 1) {
                const firstFlight = [selectedFlights[0]];
                updateReconciliationData(
                    `${fieldPath}.matched_flight_ids`,
                    firstFlight.map((f) => f.id),
                );
                updateReconciliationData(`${fieldPath}.matched_flights`, firstFlight);
            }
            // 切换到直接匹配时，清除分摊规则
            updateReconciliationData(`${fieldPath}.allocation_rule`, null);
        } else if (newType === "RULE_BASED") {
            // 切换到分摊模式时，如果未设置规则，设置默认规则
            if (!currentItem?.allocation_rule) {
                updateReconciliationData(`${fieldPath}.allocation_rule`, "BY_FLIGHT_COUNT");
            }
        }
    };

    // 处理分摊规则变更 (实时更新)
    const handleAllocationRuleChange = (e) => {
        if (!selectedLineItem) return;
        const { invoiceIndex, sectionIndex, itemIndex } = selectedLineItem;
        const fieldPath = `invoices[${invoiceIndex}].sections[${sectionIndex}].line_items[${itemIndex}]`;
        const newRule = e.target.value;

        updateReconciliationData(`${fieldPath}.allocation_rule`, newRule);

        // 切换到按加油量分摊时，若无 weight_source 则按同发票产品编码推断默认值（doc 70 §6.3）
        if (newRule === ALLOCATION_RULE.BY_FUEL_QUANTITY && !currentItem?.weight_source) {
            const defaultProductCodes = inferDefaultProductCodes(invoiceLineItems);
            if (defaultProductCodes.length > 0) {
                updateReconciliationData(`${fieldPath}.weight_source`, {
                    product_codes: defaultProductCodes,
                    field: "quantity",
                });
            }
        }
    };

    // 渲染工具箱标题栏
    const renderToolboxHeader = () => {
        if (currentItem) {
            const item = currentItem;
            const quantityDisplay = item.unit ? `${item.quantity ?? "-"} ${item.unit}` : item.quantity ?? "-";
            return (
                <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", background: "#e6f7ff" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Text strong style={{ fontSize: 16, maxWidth: "70%" }} ellipsis={{ tooltip: item.description }}>
                            {item.description || "未命名项目"}
                        </Text>
                        <Text strong style={{ fontSize: 16, color: "#1890ff" }}>
                            {item.total_price !== undefined ? `$${Number(item.total_price).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                        </Text>
                    </div>
                    <div style={{ marginTop: 4, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                        <Text type="secondary">
                            数量: {quantityDisplay} × 单价: {item.unit_price}
                        </Text>
                        {currentValidationStale ? (
                            <Tag color="blue">已修改，待重新验证航班</Tag>
                        ) : (
                            <Tag color={currentAnomalies.length > 0 ? "orange" : currentItemModified ? "blue" : "green"}>{currentAnomalies.length > 0 ? "需复核" : currentItemModified ? "已修改" : "正常"}</Tag>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0" }}>
                <Title level={5} style={{ margin: 0 }}>
                    核对助手
                </Title>
            </div>
        );
    };

    // 渲染空状态
    const renderEmptyState = () => (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "#999" }}>
            <EditOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <Paragraph>
                点击主工作区中的任意行项目，
                <br />
                这里将显示相应的决策辅助工具
            </Paragraph>
        </div>
    );

    // 渲染航班选择区域
    const renderFlightSelection = () => (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Text strong>航班选择</Text>
                <Button
                    icon={<ExpandOutlined />}
                    size="small"
                    onClick={() => {
                        setTempSelectedFlights(selectedFlights);
                        setFlightSearchModalVisible(true);
                    }}
                >
                    展开搜索
                </Button>
            </div>

            {/* 快捷筛选条件 */}
            <Card size="small" style={{ marginBottom: 12, background: "#fafafa" }}>
                <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        <InfoCircleOutlined style={{ marginRight: 4 }} />
                        智能筛选 (基于AI线索) - 自动搜索
                    </Text>
                </div>

                <Space direction="vertical" style={{ width: "100%" }} size="small">
                    {/* 飞机注册号 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Checkbox
                            checked={quickFilters.aircraft_registration.enabled}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    aircraft_registration: { ...prev.aircraft_registration, enabled: e.target.checked },
                                }))
                            }
                        >
                            <Text style={{ fontSize: 12, width: 80, display: "inline-block" }}>飞机注册号</Text>
                        </Checkbox>
                        <AircraftSelector
                            size="small"
                            placeholder="选择或搜索飞机"
                            value={quickFilters.aircraft_registration.value}
                            onChange={(value) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    aircraft_registration: { ...prev.aircraft_registration, value: value },
                                }))
                            }
                            disabled={!quickFilters.aircraft_registration.enabled}
                            style={{ flex: 1 }}
                            valueType="registration"
                            displayMode="simple"
                            allowClear
                        />
                    </div>

                    {/* 航班号 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Checkbox
                            checked={quickFilters.flight_number.enabled}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    flight_number: { ...prev.flight_number, enabled: e.target.checked },
                                }))
                            }
                        >
                            <Text style={{ fontSize: 12, width: 80, display: "inline-block" }}>航班号</Text>
                        </Checkbox>
                        <Input
                            size="small"
                            placeholder="如: FM123"
                            value={quickFilters.flight_number.value}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    flight_number: { ...prev.flight_number, value: e.target.value },
                                }))
                            }
                            disabled={!quickFilters.flight_number.enabled}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* 执行日期 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Checkbox
                            checked={quickFilters.flight_date.enabled}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    flight_date: { ...prev.flight_date, enabled: e.target.checked },
                                }))
                            }
                        >
                            <Text style={{ fontSize: 12, width: 80, display: "inline-block" }}>执行日期</Text>
                        </Checkbox>
                        <Input
                            size="small"
                            type="date"
                            value={quickFilters.flight_date.value ? quickFilters.flight_date.value.split("T")[0] : ""}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    flight_date: { ...prev.flight_date, value: e.target.value },
                                }))
                            }
                            disabled={!quickFilters.flight_date.enabled}
                            style={{ flex: 1 }}
                        />
                        {quickFilters.flight_date.enabled && quickFilters.flight_date.value && (
                            <Text type="secondary" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                                ±3天
                            </Text>
                        )}
                    </div>

                    {/* 起飞机场 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Checkbox
                            checked={quickFilters.departure_airport.enabled}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    departure_airport: { ...prev.departure_airport, enabled: e.target.checked },
                                }))
                            }
                        >
                            <Text style={{ fontSize: 12, width: 80, display: "inline-block" }}>起飞机场</Text>
                        </Checkbox>
                        <Input
                            size="small"
                            placeholder="如: PEK"
                            value={quickFilters.departure_airport.value}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    departure_airport: { ...prev.departure_airport, value: e.target.value },
                                }))
                            }
                            disabled={!quickFilters.departure_airport.enabled}
                            style={{ flex: 1 }}
                        />
                    </div>

                    {/* 到达机场 */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Checkbox
                            checked={quickFilters.arrival_airport.enabled}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    arrival_airport: { ...prev.arrival_airport, enabled: e.target.checked },
                                }))
                            }
                        >
                            <Text style={{ fontSize: 12, width: 80, display: "inline-block" }}>到达机场</Text>
                        </Checkbox>
                        <Input
                            size="small"
                            placeholder="如: SHA"
                            value={quickFilters.arrival_airport.value}
                            onChange={(e) =>
                                setQuickFilters((prev) => ({
                                    ...prev,
                                    arrival_airport: { ...prev.arrival_airport, value: e.target.value },
                                }))
                            }
                            disabled={!quickFilters.arrival_airport.enabled}
                            style={{ flex: 1 }}
                        />
                    </div>
                </Space>
            </Card>

            {/* 航班列表 - 紧凑展示 */}
            <div style={{ maxHeight: 250, overflowY: "auto" }}>
                {flightsLoading ? (
                    <div style={{ textAlign: "center", padding: 20 }}>
                        <Text type="secondary">搜索中...</Text>
                    </div>
                ) : flights.length > 0 ? (
                    flights.slice(0, 8).map((flight) => {
                        const departureTimeUtc = flight.atd || flight.std;
                        const arrivalTimeUtc = flight.ata || flight.sta;
                        const isSelected = selectedFlights.some((f) => f.id === flight.id);

                        return (
                            <Tooltip
                                key={flight.id}
                                title={
                                    <div>
                                        <div>
                                            <strong>航班号:</strong> {flight.flight_number}
                                        </div>
                                        <div>
                                            <strong>注册号:</strong> {flight.aircraft_reg || "未分配"}
                                        </div>
                                        <div>
                                            <strong>航段:</strong> {flight.departure_airport} → {flight.arrival_airport}
                                        </div>
                                        <div>
                                            <strong>起飞时间:</strong> {departureTimeUtc ? <FlightTimeDisplay utcTime={departureTimeUtc} airportCode={flight.departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={true} /> : "未定"}
                                        </div>
                                        <div>
                                            <strong>到达时间:</strong> {arrivalTimeUtc ? <FlightTimeDisplay utcTime={arrivalTimeUtc} airportCode={flight.arrival_airport} airportsMap={airportsMap} baseUtcTime={departureTimeUtc} format="MM-DD HH:mm" showTimezone={true} /> : "未定"}
                                        </div>
                                        <div>
                                            <strong>状态:</strong> {flight.status || "未知"}
                                        </div>
                                    </div>
                                }
                                placement="left"
                            >
                                <div
                                    style={{
                                        padding: "6px 10px",
                                        marginBottom: 4,
                                        border: isSelected ? "2px solid #1890ff" : "1px solid #e8e8e8",
                                        borderRadius: 4,
                                        cursor: "pointer",
                                        background: isSelected ? "#e6f7ff" : "#fff",
                                        transition: "all 0.2s",
                                        boxShadow: isSelected ? "0 2px 4px rgba(24, 144, 255, 0.2)" : "none",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.background = "#fafafa";
                                            e.currentTarget.style.borderColor = "#d9d9d9";
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.background = "#fff";
                                            e.currentTarget.style.borderColor = "#e8e8e8";
                                        }
                                    }}
                                    onClick={() => handleFlightSelection(flight, !isSelected)}
                                >
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        {/* 左侧：飞机 + 航班号 + 航线 */}
                                        <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 4 }}>
                                            <AircraftTag registration={flight.aircraft_reg} style={{ margin: 0, fontSize: 11 }} />
                                            <Text strong style={{ fontSize: 13, color: "#1890ff", minWidth: 48 }}>
                                                {flight.flight_number}
                                            </Text>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {flight.departure_airport} → {flight.arrival_airport}
                                            </Text>
                                        </div>

                                        {/* 右侧：起飞时间 - 显示日期和时间 */}
                                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                            {departureTimeUtc ? (
                                                <FlightTimeDisplay utcTime={departureTimeUtc} airportCode={flight.departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={false} />
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    未定
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Tooltip>
                        );
                    })
                ) : (
                    <Empty description="未找到匹配的航班" size="small" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            💡 尝试取消勾选部分筛选条件
                        </Text>
                    </Empty>
                )}
            </div>

            {flights.length > 8 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    显示前8个结果，共{flights.length}个航班。点击"展开搜索"查看更多。
                </Text>
            )}
        </div>
    );

    // 清空所有已选航班
    const clearAllSelectedFlights = () => {
        if (!selectedLineItem) return;
        applyFlightSelection([]);
    };

    // 渲染已选航班概要
    const renderSelectedFlightsSummary = () => {
        if (selectedFlights.length === 0) return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Text strong>已选航班 ({selectedFlights.length})</Text>
                    <Button type="link" size="small" danger onClick={clearAllSelectedFlights} style={{ padding: 0 }}>
                        清空
                    </Button>
                </div>
                <div style={{ marginTop: 8 }}>
                    {selectedFlights.map((flight) => (
                        <Tag key={flight.id} closable onClose={() => handleFlightSelection(flight, false)} style={{ marginBottom: 4 }}>
                            {flight.flight_number}
                        </Tag>
                    ))}
                </div>
            </div>
        );
    };

    // 渲染分摊规则选择
    const renderAllocationRules = () => {
        if (allocationType !== "RULE_BASED") return null;

        return (
            <div style={{ marginBottom: 16 }}>
                <Text strong>分摊规则</Text>
                {!allocationRule && (
                    <Alert type="warning" showIcon message="规则分摊项必须选择分摊规则，否则无法提交入库" style={{ marginTop: 8 }} />
                )}
                <Radio.Group value={allocationRule} onChange={handleAllocationRuleChange} style={{ marginTop: 8, display: "block" }}>
                    <Radio value={ALLOCATION_RULE.BY_FLIGHT_COUNT} style={{ display: "block", marginBottom: 8 }}>
                        {ALLOCATION_RULE_LABELS[ALLOCATION_RULE.BY_FLIGHT_COUNT]}
                    </Radio>
                    <Radio value={ALLOCATION_RULE.BY_FUEL_QUANTITY} style={{ display: "block", marginBottom: 8 }}>
                        {ALLOCATION_RULE_LABELS[ALLOCATION_RULE.BY_FUEL_QUANTITY]}
                    </Radio>
                    <Radio value={ALLOCATION_RULE.BY_FLIGHT_HOURS} style={{ display: "block", marginBottom: 8 }}>
                        {ALLOCATION_RULE_LABELS[ALLOCATION_RULE.BY_FLIGHT_HOURS]}
                    </Radio>
                    <Radio value={ALLOCATION_RULE.BY_AIRCRAFT_TYPE} style={{ display: "block", marginBottom: 8 }}>
                        {ALLOCATION_RULE_LABELS[ALLOCATION_RULE.BY_AIRCRAFT_TYPE]}
                    </Radio>
                    <Radio value={ALLOCATION_RULE.MANUAL} style={{ display: "block" }}>
                        {ALLOCATION_RULE_LABELS[ALLOCATION_RULE.MANUAL]}
                    </Radio>
                </Radio.Group>
                {/* BY_FUEL_QUANTITY 分摊预览三要素：规则名(Radio 选中态) + 每航班分摊 + 来源追溯（doc 70 §6.2） */}
                {allocationRule === ALLOCATION_RULE.BY_FUEL_QUANTITY && renderFuelQuantityPreview()}
            </div>
        );
    };

    // 渲染 BY_FUEL_QUANTITY 分摊预览三要素（每航班聚合油量/权重/金额 + 来源追溯 + 降级告警）
    const renderFuelQuantityPreview = () => {
        if (!fuelQuantityPreview) return null;
        const {
            degraded,
            reason,
            detail,
            flightAllocations,
            totalQuantity,
            totalAmount,
            sourceItemIds,
            weightSource,
        } = fuelQuantityPreview;
        const unit = currentItem?.unit || "";

        return (
            <div style={{ marginTop: 12, padding: 12, background: "#fafafa", border: "1px solid #f0f0f0", borderRadius: 6 }}>
                {/* 降级告警（doc 70 §6.4） */}
                {degraded && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 8 }}
                        message="本次分摊已降级为按数量平均分摊"
                        description={`原因：${ALLOCATION_DEGRADED_REASONS[reason] || reason}${detail ? `（${detail}）` : ""}`}
                    />
                )}
                {/* 要素② 每航班分摊预览 */}
                <div style={{ marginBottom: 6 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {degraded
                            ? "降级预览（按数量平均分摊）："
                            : `按加油量加权分摊（总油量 ${totalQuantity != null ? totalQuantity.toLocaleString() : "-"} ${unit}）`.trim()}
                    </Text>
                </div>
                {flightAllocations.map((fa) => {
                    const label = flightNumberMap[String(fa.flightId)] || flightNumberMap[fa.flightId] || `航班 ${fa.flightId}`;
                    return (
                        <div key={String(fa.flightId)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 13 }}>
                            <span>{label}</span>
                            <span>
                                {!degraded && fa.aggregatedQuantity !== null && (
                                    <Text type="secondary">
                                        {fa.aggregatedQuantity.toLocaleString()} {unit} ({fa.percentage.toFixed(2)}%) →{" "}
                                    </Text>
                                )}
                                {degraded && fa.percentage != null && (
                                    <Text type="secondary">{fa.percentage.toFixed(2)}% → </Text>
                                )}
                                <Text strong>
                                    {fa.amount !== null
                                        ? `$${fa.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                        : "-"}
                                </Text>
                            </span>
                        </div>
                    );
                })}
                {totalAmount !== null && (
                    <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px dashed #e8e8e8", textAlign: "right", fontSize: 12 }}>
                        <Text type="secondary">分摊总额：</Text>
                        <Text strong>${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>
                    </div>
                )}
                {/* 要素③ 来源追溯（可折叠） */}
                <Collapse ghost style={{ marginTop: 4 }}>
                    <Panel header={<Text type="secondary" style={{ fontSize: 12 }}>来源追溯</Text>} key="source">
                        <div style={{ fontSize: 12, color: "#666" }}>
                            <div style={{ marginBottom: 6 }}>
                                <Text type="secondary">权重来源产品编码：</Text>
                                <Space size={4} wrap>
                                    {(weightSource?.productCodes || []).map((code) => (
                                        <Tag key={code} color="orange">{code}</Tag>
                                    ))}
                                </Space>
                                <Text type="secondary">（按 {weightSource?.field || "quantity"} 字段聚合）</Text>
                            </div>
                            <div>
                                <Text type="secondary">聚合来源行：</Text>
                                {sourceItemIds.length > 0 ? (
                                    <Text code>{sourceItemIds.join(", ")}</Text>
                                ) : (
                                    <Text type="warning">无匹配来源行</Text>
                                )}
                            </div>
                        </div>
                    </Panel>
                </Collapse>
            </div>
        );
    };

    // 渲染匹配与分摊Tab内容
    const renderAllocationTab = () => {
        if (!currentItem) return renderEmptyState();

        const item = currentItem;

        return (
            <div>
                {/* 顶部状态区 */}
                <div style={{ marginBottom: 16 }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                        {currentValidationStale ? (
                            <Alert message="该行已修改，待重新验证航班" description="当前行的航班或费用字段已被人工修改，旧异常与旧验证航班结果不再代表当前事实。保存后请重新触发验证航班。" type="info" showIcon size="small" />
                        ) : (
                            currentAnomalies.length > 0 && <Alert message="匹配问题" description={`航班信息 "${item.additional_info?.raw_flight_text || ""}" 需要人工确认`} type="warning" showIcon size="small" />
                        )}
                    </Space>
                </div>

                {/* 分摊类型选择 */}
                <div style={{ marginBottom: 16 }}>
                    <Text strong>分摊类型</Text>
                    <Radio.Group value={allocationType} onChange={handleAllocationTypeChange} style={{ marginTop: 8, display: "block" }}>
                        <Radio value="DIRECT" style={{ display: "block", marginBottom: 8 }}>
                            直接匹配 - 费用归属于单个航班
                        </Radio>
                        <Radio value="RULE_BASED" style={{ display: "block" }}>
                            分摊匹配 - 费用在多个航班间分摊
                        </Radio>
                    </Radio.Group>
                </div>

                {/* 航班选择区域 */}
                {renderFlightSelection()}

                {/* 已选航班概要 */}
                {renderSelectedFlightsSummary()}

                {/* 分摊规则选择 */}
                {renderAllocationRules()}
            </div>
        );
    };

    // 渲染合同Tab内容（占位）
    const renderContractTab = () => (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "#999" }}>
            <InfoCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <Title level={4} style={{ color: "#999" }}>
                合同信息
            </Title>
            <Paragraph>此功能正在开发中...</Paragraph>
        </div>
    );

    // 渲染订单Tab内容（占位）
    const renderOrderTab = () => (
        <div style={{ padding: "40px 20px", textAlign: "center", color: "#999" }}>
            <InfoCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <Title level={4} style={{ color: "#999" }}>
                订单信息
            </Title>
            <Paragraph>此功能正在开发中...</Paragraph>
        </div>
    );

    // 航班搜索模态框
    const renderFlightSearchModal = () => (
        <Modal
            title="航班搜索 - 高级模式"
            visible={flightSearchModalVisible}
            onCancel={closeAdvancedFlightSearch}
            width={900}
            footer={[
                <Button key="cancel" onClick={closeAdvancedFlightSearch}>
                    取消
                </Button>,
                <Button
                    key="confirm"
                    type="primary"
                    onClick={() => {
                        if (!selectedLineItem) return;
                        applyFlightSelection(tempSelectedFlights);
                        setFlightSearchModalVisible(false);
                    }}
                >
                    确认选择
                </Button>,
            ]}
        >
            {/* 快捷筛选条件 - 模态框版本 */}
            <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
                <Space direction="vertical" style={{ width: "100%" }} size="small">
                    <div style={{ display: "flex", gap: 16 }}>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                飞机注册号
                            </Text>
                            <Input
                                size="small"
                                placeholder="如: B-1234"
                                value={quickFilters.aircraft_registration.value}
                                onChange={(e) =>
                                    setQuickFilters((prev) => ({
                                        ...prev,
                                        aircraft_registration: { ...prev.aircraft_registration, value: e.target.value },
                                    }))
                                }
                                prefix={
                                    <Checkbox
                                        checked={quickFilters.aircraft_registration.enabled}
                                        onChange={(e) =>
                                            setQuickFilters((prev) => ({
                                                ...prev,
                                                aircraft_registration: { ...prev.aircraft_registration, enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                航班号
                            </Text>
                            <Input
                                size="small"
                                placeholder="如: FM123"
                                value={quickFilters.flight_number.value}
                                onChange={(e) =>
                                    setQuickFilters((prev) => ({
                                        ...prev,
                                        flight_number: { ...prev.flight_number, value: e.target.value },
                                    }))
                                }
                                prefix={
                                    <Checkbox
                                        checked={quickFilters.flight_number.enabled}
                                        onChange={(e) =>
                                            setQuickFilters((prev) => ({
                                                ...prev,
                                                flight_number: { ...prev.flight_number, enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                }
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 16 }}>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                执行日期 (±3天)
                            </Text>
                            <Input
                                size="small"
                                type="date"
                                value={quickFilters.flight_date.value ? quickFilters.flight_date.value.split("T")[0] : ""}
                                onChange={(e) =>
                                    setQuickFilters((prev) => ({
                                        ...prev,
                                        flight_date: { ...prev.flight_date, value: e.target.value },
                                    }))
                                }
                                prefix={
                                    <Checkbox
                                        checked={quickFilters.flight_date.enabled}
                                        onChange={(e) =>
                                            setQuickFilters((prev) => ({
                                                ...prev,
                                                flight_date: { ...prev.flight_date, enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                起飞机场
                            </Text>
                            <Input
                                size="small"
                                placeholder="如: PEK"
                                value={quickFilters.departure_airport.value}
                                onChange={(e) =>
                                    setQuickFilters((prev) => ({
                                        ...prev,
                                        departure_airport: { ...prev.departure_airport, value: e.target.value },
                                    }))
                                }
                                prefix={
                                    <Checkbox
                                        checked={quickFilters.departure_airport.enabled}
                                        onChange={(e) =>
                                            setQuickFilters((prev) => ({
                                                ...prev,
                                                departure_airport: { ...prev.departure_airport, enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                到达机场
                            </Text>
                            <Input
                                size="small"
                                placeholder="如: SHA"
                                value={quickFilters.arrival_airport.value}
                                onChange={(e) =>
                                    setQuickFilters((prev) => ({
                                        ...prev,
                                        arrival_airport: { ...prev.arrival_airport, value: e.target.value },
                                    }))
                                }
                                prefix={
                                    <Checkbox
                                        checked={quickFilters.arrival_airport.enabled}
                                        onChange={(e) =>
                                            setQuickFilters((prev) => ({
                                                ...prev,
                                                arrival_airport: { ...prev.arrival_airport, enabled: e.target.checked },
                                            }))
                                        }
                                    />
                                }
                            />
                        </div>
                    </div>

                    <div style={{ textAlign: "right" }}>
                        <Button type="primary" icon={<SearchOutlined />} loading={flightsLoading} onClick={() => executeSearch()}>
                            搜索
                        </Button>
                    </div>
                </Space>
            </Card>

            <Table
                dataSource={flights}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 10 }}
                loading={flightsLoading}
                rowSelection={{
                    type: allocationType === "DIRECT" ? "radio" : "checkbox",
                    selectedRowKeys: tempSelectedFlights.map((f) => f.id),
                    onChange: (selectedRowKeys, selectedRows) => {
                        setTempSelectedFlights(selectedRows);
                    },
                }}
                columns={[
                    { title: "航班号", dataIndex: "flight_number", key: "flight_number" },
                    {
                        title: "航线",
                        key: "route",
                        render: (_, record) => `${record.departure_airport}-${record.arrival_airport}`,
                    },
                    {
                        title: "起飞时间",
                        key: "departure_time",
                        render: (_, record) => {
                            const departureTimeUtc = record.atd || record.std;
                            return departureTimeUtc ? <FlightTimeDisplay utcTime={departureTimeUtc} airportCode={record.departure_airport} airportsMap={airportsMap} format="MM-DD HH:mm" showTimezone={true} /> : "-";
                        },
                    },
                    {
                        title: "到达时间",
                        key: "arrival_time",
                        render: (_, record) => {
                            const departureTimeUtc = record.atd || record.std;
                            const arrivalTimeUtc = record.ata || record.sta;
                            return arrivalTimeUtc ? <FlightTimeDisplay utcTime={arrivalTimeUtc} airportCode={record.arrival_airport} airportsMap={airportsMap} baseUtcTime={departureTimeUtc} format="MM-DD HH:mm" showTimezone={true} /> : "-";
                        },
                    },
                    { title: "机型", dataIndex: "aircraft_type", key: "aircraft_type" },
                    { title: "注册号", dataIndex: "aircraft_reg", key: "aircraft_reg" },
                ]}
            />
        </Modal>
    );

    return (
        <>
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                {/* 选中项目的信息概要 */}
                {renderToolboxHeader()}

                {/* 内容区 */}
                <div style={{ flex: 1, overflow: "auto" }}>
                    {currentItem ? (
                        <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ height: "100%" }} tabBarStyle={{ paddingLeft: 16, paddingRight: 16, marginBottom: 0 }}>
                            <TabPane tab="匹配与分摊" key="allocation" style={{ padding: "0 16px 16px" }}>
                                {renderAllocationTab()}
                            </TabPane>
                            <TabPane tab="合同" key="contract">
                                {renderContractTab()}
                            </TabPane>
                            <TabPane tab="订单" key="order">
                                {renderOrderTab()}
                            </TabPane>
                        </Tabs>
                    ) : (
                        renderEmptyState()
                    )}
                </div>
            </div>

            {/* 航班搜索模态框 */}
            {renderFlightSearchModal()}
        </>
    );
};

export default ReconciliationTools;
